import { Component, ElementRef, EventEmitter, Input, Output, AfterViewInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

export type SelectionMode = 'none' | 'rectangle' | 'polygon' | 'freehand';

@Component({
	selector: 'bp-map-controls',
	standalone: true,
	imports: [CommonModule, NgbTooltipModule],
	templateUrl: './map-controls.component.html',
	styleUrls: ['./map-controls.component.css'],
})
export class MapControlsComponent implements AfterViewInit, OnDestroy, OnChanges {
	// Zoom
	@Input() zoomLevel = 0;
	@Input() zoomToWindowActive = false;
	@Output() zoomIn = new EventEmitter<void>();
	@Output() zoomOut = new EventEmitter<void>();
	@Output() gpsHome = new EventEmitter<void>();
	@Output() fitBounds = new EventEmitter<void>();
	@Output() zoomToSelection = new EventEmitter<void>();
	@Output() zoomToFilter = new EventEmitter<void>();
	@Output() zoomToWindow = new EventEmitter<void>();

	// Filter
	@Input() filteredCount = 0;

	// Selection
	@Input() selectionMode: SelectionMode = 'none';
	@Input() selectionCount = 0;
	@Input() polygonPoints = 0;
	@Output() selectionModeChange = new EventEmitter<SelectionMode>();
	@Output() finishPolygon = new EventEmitter<void>();
	@Output() clearSelection = new EventEmitter<void>();
	@Output() selectFiltered = new EventEmitter<void>();
	@Output() printSelection = new EventEmitter<void>();
	@Output() deleteSelection = new EventEmitter<void>();

	// WMS Info
	@Input() wmsInfoActive = false;
	@Input() hasVisibleWmsLayers = false;
	@Output() toggleWmsInfo = new EventEmitter<void>();

	// New Point
	@Input() canCreatePoint = false;
	@Input() newPointOnMapActive = false;
	@Output() newPointAtLocation = new EventEmitter<void>();
	@Output() newPointOnMap = new EventEmitter<void>();

	// Draw
	@Input() showDrawControls = false;
	@Input() canDraw = true;  // Gäste dürfen nicht zeichnen
	@Input() networkAvailable = true;  // Netzwerk verfügbar (im Online-Modus)
	@Input() drawEnabled = false;
	@Input() drawPolygonPoints = 0;  // Anzahl der Punkte beim Polygon-Zeichnen
	@Input() freehandEnabled = false;
	@Input() lineEnabled = false;
	@Input() lineFreehandEnabled = false;
	@Input() editEnabled = false;
	@Input() deleteEnabled = false;
	@Input() gpsTrackingPolygonEnabled = false;
	@Input() gpsTrackingLineEnabled = false;
	@Input() hasDrawnPolygon = false;  // Wurde tatsächlich ein Polygon gezeichnet?
	@Input() hasDrawnLine = false;     // Wurde tatsächlich eine Linie gezeichnet?
	@Output() toggleDraw = new EventEmitter<void>();
	@Output() finishDrawPolygon = new EventEmitter<void>();  // Polygon-Zeichnung abschließen
	@Output() toggleFreehand = new EventEmitter<void>();
	@Output() toggleLine = new EventEmitter<void>();
	@Output() toggleLineFreehand = new EventEmitter<void>();
	@Output() toggleGpsTrackingPolygon = new EventEmitter<void>();
	@Output() toggleGpsTrackingLine = new EventEmitter<void>();
	@Output() stopGpsTracking = new EventEmitter<void>();
	@Output() editDraw = new EventEmitter<void>();
	@Output() deleteDraw = new EventEmitter<void>();
	@Output() confirmEdit = new EventEmitter<void>();  // Änderungen übernehmen
	@Output() cancelEdit = new EventEmitter<void>();   // Änderungen abbrechen
	@Output() finishDrawMode = new EventEmitter<void>();  // Zeichenmodus beenden
	@Output() deactivateAllTools = new EventEmitter<void>();

	// Expandierbare Gruppen
	zoomToExpanded = false;
	selectionExpanded = false;
	pointExpanded = false;
	polygonExpanded = false;
	lineExpanded = false;

	// Gast-Hinweis
	readonly guestHint = 'Für Gäste nicht verfügbar';
	readonly networkHint = 'Keine Netzwerkverbindung';

	/** Prüft ob Aktionen erlaubt sind (nicht Gast UND Netzwerk verfügbar) */
	get canAct(): boolean {
		return this.networkAvailable;
	}

	/** Prüft ob Zeichnen erlaubt ist (canDraw UND Netzwerk verfügbar) */
	get canDrawAndNetwork(): boolean {
		return this.canDraw && this.networkAvailable;
	}

	/** Prüft ob Punkt erstellen erlaubt ist (canCreatePoint UND Netzwerk verfügbar) */
	get canCreatePointAndNetwork(): boolean {
		return this.canCreatePoint && this.networkAvailable;
	}

	/** Tooltip für deaktivierte Aktionen (Gast-Prüfung hat Vorrang vor Netzwerk) */
	getDisabledHint(forGuest: boolean): string {
		if (forGuest && !this.canDraw) return this.guestHint;
		if (!this.networkAvailable) return this.networkHint;
		return '';
	}

	// Zoom +/- mit Einklappen aller Expandables
	onZoomIn(): void {
		this.collapseAllExpandables();
		this.zoomIn.emit();
	}

	onZoomOut(): void {
		this.collapseAllExpandables();
		this.zoomOut.emit();
	}

	// Kompakt-Modi
	compact = false;
	ultraCompact = false;

	private resizeObserver?: ResizeObserver;
	private checkPending = false;

	// Tracking für Neuberechnung bei Änderungen
	private lastSelectionCount = 0;

	constructor(private el: ElementRef, private cdr: ChangeDetectorRef) {}

	ngAfterViewInit(): void {
		// ResizeObserver um Überlappung zu erkennen
		this.resizeObserver = new ResizeObserver(() => {
			if (!this.checkPending) {
				this.checkPending = true;
				requestAnimationFrame(() => {
					this.checkOverlap();
					this.checkPending = false;
				});
			}
		});

		const parent = this.el.nativeElement.parentElement;
		if (parent) {
			this.resizeObserver.observe(parent);
		}

		// Initial prüfen
		setTimeout(() => this.checkOverlap(), 100);
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Wenn selectionCount von 0 auf >0 oder von >0 auf 0 wechselt, neu prüfen
		if (changes['selectionCount']) {
			const wasZero = this.lastSelectionCount === 0;
			const isZero = this.selectionCount === 0;
			this.lastSelectionCount = this.selectionCount;

			if (wasZero !== isZero) {
				// Sichtbare Buttons haben sich geändert - neu prüfen
				setTimeout(() => this.checkOverlap(), 50);
			}
		}

		// Gruppen einklappen wenn deren Tools deaktiviert wurden
		if (changes['selectionMode'] && this.selectionMode === 'none') {
			this.selectionExpanded = false;
		}
		// Punkt-Gruppe bleibt offen wenn Tool deaktiviert wird (damit man direkt weiteren Punkt setzen kann)
	}

	private checkOverlap(): void {
		const container = this.el.nativeElement.querySelector('.map-controls-container') as HTMLElement;
		const topLeft = this.el.nativeElement.querySelector('.controls-top-left') as HTMLElement;
		const bottomLeft = this.el.nativeElement.querySelector('.controls-bottom-left') as HTMLElement;

		if (!container || !topLeft || !bottomLeft) return;

		const containerHeight = container.getBoundingClientRect().height;

		// Tatsächliche Höhen messen statt schätzen
		const topHeight = topLeft.getBoundingClientRect().height;
		const bottomHeight = bottomLeft.getBoundingClientRect().height;
		const totalHeight = topHeight + bottomHeight;

		// Mindestabstand zwischen oben und unten
		const minGap = 40;

		// Bestimme benötigten Modus basierend auf tatsächlicher Überlappung
		let needsCompact = false;
		let needsUltraCompact = false;

		// Erst kompakt wenn wirklich zu wenig Platz
		if (totalHeight + minGap > containerHeight) {
			needsCompact = true;
			// Ultra-kompakt nur wenn auch kompakt nicht reicht (nach Skalierung ~80%)
			if (totalHeight * 0.8 + minGap > containerHeight) {
				needsUltraCompact = true;
			}
		}

		// Nur ändern wenn nötig
		if (this.compact !== needsCompact || this.ultraCompact !== needsUltraCompact) {
			this.compact = needsCompact;
			this.ultraCompact = needsUltraCompact;
			this.cdr.detectChanges();
		}
	}

	// Expandierbare Gruppen
	toggleZoomToExpanded(): void {
		// Wenn bereits expanded, einklappen und zum Handtool wechseln
		if (this.zoomToExpanded) {
			this.zoomToExpanded = false;
			this.deactivateAllTools.emit();
			return;
		}
		// Alle anderen Gruppen einklappen und Tools deaktivieren
		this.collapseAllExpandables();
		this.deactivateAllTools.emit();
		this.zoomToExpanded = true;
		// ZoomToWindow automatisch aktivieren beim Öffnen
		this.zoomToWindow.emit();
	}

	onGpsHome(): void {
		this.gpsHome.emit();
		this.zoomToExpanded = false;
	}

	onFitBounds(): void {
		this.fitBounds.emit();
		this.zoomToExpanded = false;
	}

	onZoomToSelection(): void {
		this.zoomToSelection.emit();
		this.zoomToExpanded = false;
	}

	onZoomToFilter(): void {
		this.zoomToFilter.emit();
		this.zoomToExpanded = false;
	}

	onZoomToWindow(): void {
		this.zoomToWindow.emit();
		this.zoomToExpanded = false;
	}

	toggleSelectionExpanded(): void {
		// Wenn bereits expanded oder Auswahl-Modus aktiv, einklappen und zum Handtool wechseln
		if (this.selectionExpanded || this.selectionMode !== 'none') {
			this.selectionExpanded = false;
			this.deactivateAllTools.emit();
			return;
		}
		this.selectionExpanded = true;
		this.zoomToExpanded = false;
		this.pointExpanded = false;
		this.polygonExpanded = false;
		this.lineExpanded = false;
		// Rechteck-Auswahl automatisch aktivieren beim Öffnen
		this.selectionModeChange.emit('rectangle');
	}

	onRectangleSelection(): void {
		const newMode = this.selectionMode === 'rectangle' ? 'none' : 'rectangle';
		this.selectionModeChange.emit(newMode);
		// Gruppe bleibt offen wenn aktiviert, ngOnChanges schließt wenn deaktiviert
	}

	onPolygonSelection(): void {
		const newMode = this.selectionMode === 'polygon' ? 'none' : 'polygon';
		this.selectionModeChange.emit(newMode);
	}

	onFreehandSelection(): void {
		const newMode = this.selectionMode === 'freehand' ? 'none' : 'freehand';
		this.selectionModeChange.emit(newMode);
	}

	togglePointExpanded(): void {
		// Wenn bereits expanded oder Tool aktiv, einklappen und zum Handtool wechseln
		if (this.pointExpanded || this.newPointOnMapActive) {
			this.pointExpanded = false;
			this.deactivateAllTools.emit();
			return;
		}
		this.pointExpanded = true;
		this.zoomToExpanded = false;
		this.selectionExpanded = false;
		this.polygonExpanded = false;
		this.lineExpanded = false;
		// Punkt manuell setzen automatisch aktivieren beim Öffnen
		this.newPointOnMap.emit();
	}

	togglePolygonExpanded(): void {
		// Wenn bereits expanded oder Tool aktiv, einklappen und zum Handtool wechseln
		if (this.polygonExpanded || this.isPolygonToolActive()) {
			this.polygonExpanded = false;
			this.deactivateAllTools.emit();
			return;
		}
		this.polygonExpanded = true;
		this.zoomToExpanded = false;
		this.selectionExpanded = false;
		this.pointExpanded = false;
		this.lineExpanded = false;
		// Freihand-Polygon automatisch aktivieren beim Öffnen
		this.toggleFreehand.emit();
	}

	toggleLineExpanded(): void {
		// Wenn bereits expanded oder Tool aktiv, einklappen und zum Handtool wechseln
		if (this.lineExpanded || this.isLineToolActive()) {
			this.lineExpanded = false;
			this.deactivateAllTools.emit();
			return;
		}
		this.lineExpanded = true;
		this.zoomToExpanded = false;
		this.selectionExpanded = false;
		this.pointExpanded = false;
		this.polygonExpanded = false;
		// Freihand-Linie automatisch aktivieren beim Öffnen
		this.toggleLineFreehand.emit();
	}

	onPointAtLocation(): void {
		this.newPointAtLocation.emit();
		this.pointExpanded = false;
	}

	onPointOnMap(): void {
		this.newPointOnMap.emit();
		// Gruppe bleibt offen, ngOnChanges schließt wenn deaktiviert
	}

	onPolygonNodes(): void {
		this.toggleDraw.emit();
		// Gruppe bleibt offen, ngOnChanges schließt wenn deaktiviert
	}

	onPolygonFreehand(): void {
		this.toggleFreehand.emit();
	}

	onLineNodes(): void {
		this.toggleLine.emit();
	}

	onLineFreehand(): void {
		this.toggleLineFreehand.emit();
	}

	onPolygonGpsTracking(): void {
		this.toggleGpsTrackingPolygon.emit();
	}

	onLineGpsTracking(): void {
		this.toggleGpsTrackingLine.emit();
	}

	isPolygonToolActive(): boolean {
		return this.drawEnabled || this.freehandEnabled || this.gpsTrackingPolygonEnabled;
	}

	isLineToolActive(): boolean {
		return this.lineEnabled || this.lineFreehandEnabled || this.gpsTrackingLineEnabled;
	}

	isGpsTrackingActive(): boolean {
		return this.gpsTrackingPolygonEnabled || this.gpsTrackingLineEnabled;
	}

	onStopGpsTracking(): void {
		this.stopGpsTracking.emit();
	}

	onEditDraw(): void {
		// Alle expandierten Gruppen einklappen
		this.collapseAllExpandables();
		this.editDraw.emit();
	}

	onDeleteDraw(): void {
		// Alle expandierten Gruppen einklappen
		this.collapseAllExpandables();
		this.deleteDraw.emit();
	}

	private collapseAllExpandables(): void {
		this.zoomToExpanded = false;
		this.selectionExpanded = false;
		this.pointExpanded = false;
		this.polygonExpanded = false;
		this.lineExpanded = false;
	}

	isAnyToolActive(): boolean {
		return this.selectionMode !== 'none' ||
			this.zoomToWindowActive ||
			this.wmsInfoActive ||
			this.drawEnabled ||
			this.freehandEnabled ||
			this.lineEnabled ||
			this.lineFreehandEnabled ||
			this.editEnabled ||
			this.deleteEnabled ||
			this.gpsTrackingPolygonEnabled ||
			this.gpsTrackingLineEnabled ||
			this.newPointOnMapActive;
	}

	onDeactivateAllTools(): void {
		// Alle expandierten Gruppen einklappen
		this.zoomToExpanded = false;
		this.selectionExpanded = false;
		this.pointExpanded = false;
		this.polygonExpanded = false;
		this.lineExpanded = false;

		this.deactivateAllTools.emit();
	}
}
